import React, { useState, useEffect } from 'react'
import { View, Text, StyleSheet, Platform, Dimensions } from 'react-native'
import WebHeader from './WebHeader'
import WebSidebar from './WebSidebar'
import RightSidebar from './RightSidebar'
import { Notifications } from '../notifications/Notifications'
import ConfigurationPanel from './ConfigurationPanel'
import PostWithComments from '../posts/PostWithComments'
import theme from '../../../theme'
import { useFooterAppInitializer } from './hooks/useFooterAppInitializer'
import { RightSidebarProvider, useRightSidebar } from './context/RightSidebarContext'
import { getLanguage } from '../appBar/context/LocalizationContext'
import { useWebInitialLoad } from './hooks/useWebInitialLoad'
import WebInitialLoader from './WebInitialLoader'
import { useDimensionsStore } from '../../redux/zustand/useDimensionStore'
import { SIDEBARFULLWIDTH, SIDEBARSMALLWIDTH } from '../customUtils/AppConstants'
import { sideBarStore } from '@/redux/zustand/sideBarStore'

const WebLayoutContent = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(true) // Desktop: abierto = expandido; Mobile: visible
    const [isMobile, setIsMobile] = useState(false) // Para detectar pantallas pequeñas
    const { activeRightSidebar, sidebarData, closeRightSidebar } = useRightSidebar()
    const { languageFile } = getLanguage()

    // Sistema de carga inicial optimizado
    const loadingState = useWebInitialLoad()

    // Inicializar footer_app para web (mantener por compatibilidad)
    const { footer_app, isLoaded } = useFooterAppInitializer()

    // Log del estado de carga
    useEffect(() => {
        console.log('[WebLayout] Loading state:', {
            isLoading: loadingState.isLoading,
            progress: loadingState.progress,
            message: loadingState.message,
        })
    }, [loadingState.isLoading, loadingState.progress])
    // const setDimensions = useDimensionsStore((state) => state.setDimensions)

    useEffect(() => {
        if (Platform.OS === 'web') {
            const checkScreenSize = () => {
                const { width } = Dimensions.get('window')
                const mobile = width < 768 // Consideramos móvil si es menor a 768px
                setIsMobile(mobile)
                // En móvil, sidebar cerrado por defecto
                if (mobile) {
                    setSidebarOpen(false)
                }
            }

            checkScreenSize()

            // Escuchar cambios de tamaño de ventana
            const subscription = Dimensions.addEventListener('change', checkScreenSize)

            return () => subscription?.remove()
        }
    }, [])
    //@FIXME: Unificar ambos useEffects, ya que ambos escuchan cambios de dimensiones
    // useEffect(() => {
    //     const subscription = Dimensions.addEventListener('change', ({ window }) => {
    //         setDimensions(window)
    //         console.log('[WebLayout] Window dimensions changed:', window)
    //     })

    //     return () => subscription?.remove()
    // }, [setDimensions])

    if (Platform.OS !== 'web') {
        return children // En mobile, renderiza solo el children sin layout
    }

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen)
    }
    const { sideBarMarginRight } = sideBarStore()
    return (
        <View style={styles.container}>
            <div className='web-header' style={styles.headerWeb}>
                <WebHeader onToggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />
            </div>
            <View style={styles.content}>
                {/* Overlay para cerrar el sidebar en modo móvil */}
                {sidebarOpen && isMobile && <div className='sidebar-overlay' style={styles.overlay} onClick={toggleSidebar} />}

                <div
                    className='web-sidebar'
                    style={{
                        ...styles.sidebarWeb,
                        width: !isMobile ? (sidebarOpen ? SIDEBARFULLWIDTH : SIDEBARSMALLWIDTH) : SIDEBARFULLWIDTH,
                        transform: isMobile ? (sidebarOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
                        transition: 'width 0.25s ease, transform 0.25s ease',
                        zIndex: isMobile ? 1001 : 999,
                        overflow: 'hidden',
                    }}
                >
                    <WebSidebar collapsed={!sidebarOpen && !isMobile} isMobile={isMobile} />
                </div>
                <div
                    className='web-main-content'
                    style={{
                        ...styles.mainContentWeb,
                        marginLeft: !isMobile ? (sidebarOpen ? SIDEBARFULLWIDTH + sideBarMarginRight : SIDEBARSMALLWIDTH) : 0,
                        transition: 'margin-left 0.25s ease',
                    }}
                >
                    {children}
                </div>

                {/* Right Sidebar - Notifications */}
                <RightSidebar
                    isOpen={activeRightSidebar === 'notifications'}
                    onClose={closeRightSidebar}
                    title={languageFile.notifications || 'Notificaciones'}
                    width={420}
                >
                    <Notifications closeSearch={closeRightSidebar} />
                </RightSidebar>

                {/* Right Sidebar - Configuration */}
                <RightSidebar
                    isOpen={activeRightSidebar === 'configuration'}
                    onClose={closeRightSidebar}
                    title={languageFile.configuration || 'Configuración'}
                    width={400}
                >
                    <ConfigurationPanel onClose={closeRightSidebar} />
                </RightSidebar>

                {/* Right Sidebar - Comments */}
                <RightSidebar
                    isOpen={activeRightSidebar === 'comments'}
                    onClose={closeRightSidebar}
                    title={languageFile.comments || 'Comentarios'}
                    width={480}
                    overlayPointerEvents='none'
                >
                    {sidebarData?.post && (
                        <PostWithComments post={sidebarData.post} closeSheet={closeRightSidebar} onPostUpdate={sidebarData.onPostUpdate} />
                    )}
                </RightSidebar>
            </View>

            {/* Loader de carga inicial - Se muestra sobre todo el contenido */}
            <WebInitialLoader
                progress={loadingState.progress}
                message={loadingState.message}
                subMessage={loadingState.subMessage}
                visible={loadingState.isLoading}
            />
        </View>
    )
}

const WebLayout = ({ children }) => {
    return (
        <RightSidebarProvider>
            <WebLayoutContent>{children}</WebLayoutContent>
        </RightSidebarProvider>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background || '#f5f5f5',
        ...(Platform.OS === 'web' && {
            minHeight: '100vh',
            width: '100vw',
        }),
    },
    content: {
        flex: 1,
        flexDirection: 'row',
        marginTop: 60, // Altura del header
    },
    headerWeb: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        height: 60,
    },
    sidebarWeb: {
        position: 'fixed',
        left: 0,
        top: 60,
        bottom: 0,
        zIndex: 999,
        backgroundColor: theme.colors.background || '#fff',
        borderRightWidth: 1,
        borderRightStyle: 'solid',
        borderRightColor: '#e0e0e0',
    },
    mainContentWeb: {
        flex: 1,
        backgroundColor: theme.colors.background || '#f5f5f5',
        overflowY: 'auto',
        paddingTop: 20,
        width: '100%',
    },
    overlay: {
        position: 'fixed',
        top: 60,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        transition: 'opacity 0.3s ease-in-out',
    },
})

export default WebLayout
